import {
  Spinner,
  Button,
  Datepicker,
  SelectDropdown,
  AgGrid,
  ErrorMessage,
  MobileResponsiveGrid,
} from "@/common/components";
import { PrivateContext } from "@/routes/PrivateRoute";
import { useContext, useState, useEffect, useSelector } from "@/common/hooks";
import { useGetTransactionDetailMutation } from "@/service/OwnerService";
import dayjs from "dayjs";
import { amountFormatter } from "@/utils/Utilities";
import { setTransactionDetailsForDateRange } from "@/store/ownersSlice";

const TransactionDetails = ({ ownerData }) => {
  const { labels, accessProfileId, productSponsorId, dispatch } =
    useContext(PrivateContext);

  const [tradeDateRange, setTradeDateRange] = useState();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [asOfBalanceDateFrom, setAsOfBalanceDateFrom] = useState();
  const [asOfBalanceDateTo, setAsOfBalanceDateTo] = useState();
  const [transactionDetailsRowData, setTransactionDetailsRowData] = useState();
  const [dateError, setDateError] = useState(false);
  const [errors, setErrors] = useState({});

  const [getTransactionDetailApi, { isLoading: isFetching, error }] =
    useGetTransactionDetailMutation();

  const transactionDetailsFromStore = useSelector(
    (state) => state?.ownersReducer?.transactionDetailsForDateRange
  );

  const handleSelectValue = (value) => {
    if (!value) {
      setTradeDateRange("");
      setShowDatePicker(false);
      return;
    }

    setTradeDateRange(value);

    const today = dayjs();
    let fromDate = "";
    let toDate = today.format("YYYY/MM/DD");

    switch (dateRangeOptions[value].name) {
      case labels?.ownerInquiry?.last60Days:
        fromDate = today.subtract(60, "day").format("YYYY/MM/DD");
        setShowDatePicker(false);
        break;
      case labels?.ownerInquiry?.last90Days:
        fromDate = today.subtract(90, "day").format("YYYY/MM/DD");
        setShowDatePicker(false);
        break;
      case labels?.ownerInquiry?.yearToDate:
        fromDate = today.startOf("year").format("YYYY/MM/DD");
        setShowDatePicker(false);
        break;
      case labels?.ownerInquiry?.previousYear:
        fromDate = today
          .subtract(1, "year")
          .startOf("year")
          .format("YYYY/MM/DD");
        toDate = today.subtract(1, "year").endOf("year").format("YYYY/MM/DD");
        setShowDatePicker(false);
        break;
      case labels?.ownerInquiry?.custom:
        setShowDatePicker(true);
        return;
      default:
        return;
    }
    setAsOfBalanceDateFrom(fromDate);
    setAsOfBalanceDateTo(toDate);

    searchTransactionDetails(fromDate, toDate, value);
  };

  const handleFromDateChange = (value) => {
    const fromDate = dayjs(value);
    if (asOfBalanceDateTo) {
      const toDate = dayjs(asOfBalanceDateTo);

      if (fromDate.isAfter(toDate)) {
        setDateError(true);
        setErrors({
          ...errors,
          dateRange: labels?.ownerInquiry?.fromDateCannotBeLaterThanTo,
        });
      } else if (toDate.diff(fromDate, "year", true) > 1) {
        setDateError(true);
        setErrors({
          ...errors,
          dateRange: labels?.ownerInquiry?.dateRangeExceedError,
        });
      } else {
        setDateError(false);
        setErrors((prev) => ({ ...prev, dateRange: "" }));
      }
    } else {
      setDateError(false);
      setErrors((prev) => ({ ...prev, dateRange: "" }));
    }

    setAsOfBalanceDateFrom(value);
  };

  const handleToDateChange = (value) => {
    const fromDate = asOfBalanceDateFrom ? dayjs(asOfBalanceDateFrom) : dayjs();
    const toDate = dayjs(value);

    if (toDate.isBefore(fromDate)) {
      setDateError(true);
      setErrors({
        ...errors,
        dateRange: labels?.ownerInquiry?.toDateCannotBeEarlierThanFrom,
      });
    } else if (toDate.diff(fromDate, "year", true) > 1) {
      setDateError(true);
      setErrors({
        ...errors,
        dateRange: labels?.ownerInquiry?.dateRangeExceedError,
      });
    } else {
      setDateError(false);
      setErrors((prev) => ({ ...prev, dateRange: "" }));
    }
    setAsOfBalanceDateTo(value);
  };

  const dateRangeOptions = [
    { id: 0, name: labels?.ownerInquiry?.custom },
    { id: 1, name: labels?.ownerInquiry?.last60Days },
    { id: 2, name: labels?.ownerInquiry?.last90Days },
    { id: 3, name: labels?.ownerInquiry?.yearToDate },
    { id: 4, name: labels?.ownerInquiry?.previousYear },
  ];

  const defaultColDef = {
    suppressSizeToFit: true,
    resizable: false,
    sortable: true,
    flex: 1,
    suppressMovable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    wrapText: true,
    autoHeight: true,
  };

  const columnDefs = [
    { headerName: labels?.ownerInquiry?.tradeDate, field: "tradeDate" },
    { headerName: labels?.ownerInquiry?.postDate, field: "postedDate" },
    {
      headerName: labels?.ownerInquiry?.investmentVehicle,
      field: "investmentVehicleName",
    },
    {
      headerName: labels?.ownerInquiry?.description,
      field: "description",
    },
    {
      headerName: labels?.ownerInquiry?.amount,
      field: "amount",
      cellRenderer: amountFormatter,
    },
  ];

  const searchTransactionDetails = (
    fromDate = asOfBalanceDateFrom,
    toDate = asOfBalanceDateTo,
    dateRangeValue = tradeDateRange
  ) => {
    setAsOfBalanceDateFrom(fromDate);
    setAsOfBalanceDateTo(toDate);
    let transactionData = {
      dateRange: dateRangeValue,
      transactionDataForDateRange: null,
      fromDate,
      toDate,
    };
    dispatch(setTransactionDetailsForDateRange(transactionData));
    const {
      lineOfBusinessId,
      ownershipType,
      ownerTaxId,
      planId,
      product,
      recordkeeperRecordkeepingSystem,
    } = ownerData;

    const formattedFromDate = dayjs(fromDate).format("MM/DD/YYYY");
    const formattedToDate = dayjs(toDate).format("MM/DD/YYYY");

    const searchTransactionDetailsPayload = {
      lineOfBusinessId: lineOfBusinessId,
      ownershipTypeCode: ownershipType,
      ownerTaxId: ownerTaxId,
      planId: planId,
      externalProductId: product.externalId,
      recordKeeperName: recordkeeperRecordkeepingSystem.recordkeeperName,
      recordKeepingSystemName:
        recordkeeperRecordkeepingSystem.recordkeepingName,
      startTradeDate: formattedFromDate,
      endTradeDate: formattedToDate,
      productSponsorId: productSponsorId,
      accessProfileId: accessProfileId,
    };

    getTransactionDetailApi(searchTransactionDetailsPayload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          let transactionDetails = {
            transactionDataForDateRange: res?.transactionDetailBeans,
            dateRange: dateRangeValue,
            ...(dateRangeOptions[dateRangeValue]?.name ===
              labels?.ownerInquiry?.custom && {
              fromDate,
              toDate,
            }),
          };
          dispatch(setTransactionDetailsForDateRange(transactionDetails));
          setTransactionDetailsRowData(res.transactionDetailBeans);
          if (
            dateRangeOptions[dateRangeValue]?.name ===
            labels?.ownerInquiry?.custom
          ) {
            setAsOfBalanceDateFrom(fromDate);
            setAsOfBalanceDateTo(toDate);
          }
        }
      });
  };
  useEffect(() => {
    if (
      !transactionDetailsFromStore?.dateRange &&
      labels?.ownerInquiry?.last60Days
    ) {
      const defaultOption = dateRangeOptions.find(
        (opt) => opt.name === labels?.ownerInquiry?.last60Days
      );
      if (defaultOption) {
        handleSelectValue(defaultOption.id);
      }
    } else {
      setTransactionDetailsRowData(
        transactionDetailsFromStore?.transactionDataForDateRange
      );
      setTradeDateRange(transactionDetailsFromStore?.dateRange);
      setAsOfBalanceDateFrom(transactionDetailsFromStore?.fromDate);
      setAsOfBalanceDateTo(transactionDetailsFromStore?.toDate);
      if (transactionDetailsFromStore?.dateRange === "0")
        setShowDatePicker(true);
    }
  }, [
    transactionDetailsFromStore?.fromDate,
    transactionDetailsFromStore?.toDate,
    transactionDetailsFromStore,
    labels?.ownerInquiry?.last60Days,
  ]);
  return (
    <>
      {isFetching && <Spinner />}
      <ErrorMessage error={error} wrapperClassName="mb-3" />
      <p className="fw-bold mb-1">{labels?.ownerInquiry?.tradeDateRange}</p>
      <SelectDropdown
        id="date-range-select"
        dropdownClass="form-group"
        wrapperClassName="w-md-25 mb-3"
        selectClassName="form-control"
        labelClass="mb-1"
        dropdownData={dateRangeOptions}
        value={tradeDateRange}
        select={handleSelectValue}
        optionId="id"
        optionLabel="name"
        ariaLabel="Select date range"
        defaultOption={true}
      />
      {showDatePicker && (
        <div className="d-flex flex-column flex-md-row gap-2 pt-3">
          <div className="d-flex gap-2 justify-content-between">
            <Datepicker
              id="transactionDetailsAsOfBalanceDateFrom"
              value={asOfBalanceDateFrom}
              wrapperClassName="w-auto z-2 mb-0"
              change={handleFromDateChange}
              label={labels?.ownerInquiry?.asOfBalanceDate}
              inputClass={dateError ? "is-invalid" : ""}
            />
            <p className="mb-1 align-self-end">to</p>
            <Datepicker
              id="transactionDetailsAsOfBalanceDateTo"
              value={asOfBalanceDateTo}
              wrapperClassName="w-auto z-2 mb-0"
              change={handleToDateChange}
              label={labels?.ownerInquiry?.asOfBalanceDate}
              inputClass={`${dateError ? "is-invalid" : ""}`}
            />
          </div>

          <Button
            wrapperClassName="col-12 col-md-2 align-self-end h-auto ms-1"
            text={labels?.ownerInquiry?.search}
            buttonClassName="px-4 border-1"
            click={searchTransactionDetails}
            disabled={dateError || !asOfBalanceDateFrom || !asOfBalanceDateTo}
          />
        </div>
      )}
      {dateError && showDatePicker && errors.dateRange && (
        <p className="text-danger small mt-1">{errors.dateRange}</p>
      )}
      {transactionDetailsRowData && (
        <div>
          <AgGrid
            rowData={transactionDetailsRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            wrapperClassNames="my-4 d-none d-md-block"
            domLayout="normal"
            overlayNoRowsTemplate={labels?.ownerInquiry?.noHistoryDateRange}
          />
          <MobileResponsiveGrid
            key="ownerTransactionDetails"
            rowData={transactionDetailsRowData}
            columnDefs={columnDefs}
            noDataLabel={labels?.ownerInquiry?.noHistoryDateRange}
          />
        </div>
      )}
    </>
  );
};

export default TransactionDetails;
